import React, { useContext } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
} from '@material-ui/core';
import { Controller, FormProvider } from 'react-hook-form';

import { MAIN_CONTAINER_ID } from '../../../../constants';
import { AppGroupedSelect, AppSelect } from '../../../../components/inputs';
import { ControlledAutocomplete, ControlledTagAutocomplete } from '../../../../components/controlled-inputs';
import { DeviceContext } from '../../../../contexts';
import { UseFormReturn } from 'react-hook-form';
import { Borough } from '../../../../types';
import { FilterMode } from '../hooks/useWidgetData';

const id = 'proximity-filter-dialog-title';

interface TeamOption {
  name: string;
  value: string;
}

interface FormValues {
  filterMode: FilterMode;
  teamId: string;
  siteId: string;
  boroughs: Borough[];
  jobTitles: string[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
  methods: UseFormReturn<FormValues>;
  groupOneOptions: TeamOption[];
  groupTwoOptions: TeamOption[];
  locationOptions: { name: string; value: string }[];
  boroughs: Borough[];
  jobTitleOptions: string[];
  menuProps?: { container?: HTMLElement | null };
  teamSelectDisabled?: boolean;
  /** When in fullscreen mode, pass the fullscreen element so the dialog renders inside it */
  dialogContainer?: HTMLElement | null;
}

export const FilterDialog = ({
  isOpen,
  onClose,
  onUpdate,
  methods,
  groupOneOptions,
  groupTwoOptions,
  locationOptions,
  boroughs,
  jobTitleOptions,
  menuProps = {},
  teamSelectDisabled = false,
  dialogContainer,
}: Props) => {
  const { isMobile } = useContext(DeviceContext);

  const handleUpdate = () => {
    onUpdate();
    onClose();
  };

  const dialogContainerEl = dialogContainer ?? document.getElementById(MAIN_CONTAINER_ID);

  return (
    <Dialog
      container={dialogContainerEl}
      fullScreen={isMobile}
      open={isOpen}
      onClose={onClose}
      aria-labelledby={id}
    >
      <FormProvider {...methods}>
        <form style={{ minWidth: '600px' }} onSubmit={e => e.preventDefault()}>
          <DialogTitle id={id}>Filters</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gridGap={24} marginTop={2} minWidth={300}>
              <Controller
                control={methods.control}
                name="filterMode"
                render={({ field }) => (
                  <FormControl fullWidth variant="filled">
                    <InputLabel>View by</InputLabel>
                    <AppSelect
                      {...field}
                      value={field.value}
                      onChange={e => field.onChange(e.target.value as FilterMode)}
                      fullWidth
                      MenuProps={menuProps}
                    >
                      <MenuItem value="distance">Distance</MenuItem>
                      <MenuItem value="borough">Borough</MenuItem>
                    </AppSelect>
                  </FormControl>
                )}
              />
              {methods.watch('filterMode') === 'borough' && boroughs.length > 0 && (
                <ControlledTagAutocomplete
                  name="boroughs"
                  items={boroughs}
                  multiple
                  label="Borough"
                  margin="none"
                  creatable={false}
                  dropdownContainer={dialogContainer}
                />
              )}
              <AppGroupedSelect
                groupOneOptions={groupOneOptions}
                groupTwoOptions={groupTwoOptions}
                name="teamId"
                label="Team"
                disabled={teamSelectDisabled}
                MenuProps={menuProps}
              />
              <ControlledAutocomplete
                name="jobTitles"
                multiple
                isTags
                items={jobTitleOptions}
                label="Job titles"
              />
              {methods.watch('filterMode') === 'distance' && (
                <AppGroupedSelect
                  groupOneOptions={locationOptions}
                  groupTwoOptions={[]}
                  name="siteId"
                  label="Location"
                  MenuProps={menuProps}
                />
              )}
            </Box>
          </DialogContent>

          <DialogActions>
            <Button size="large" variant="outlined" onClick={onClose}>
              Close
            </Button>
            <Button size="large" type="button" variant="contained" color="primary" onClick={handleUpdate}>
              Update
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
